import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import {
    getAgentVoIPRegistrationInterface,
    getAgentVoIPRegistrationInterfaceTooltip,
    isNextGeneration,
    isVoIPAgentDirectCallMode,
} from '../../pages-admin/agents/AgentHelper';
import { buildLinkUrl } from '../../links/Link';
import {
    CALL_VIA_PHONE_NUMBER,
    CALL_VIA_SIP_USER_ID,
    CALLEE,
    CALLER,
    DEFAULT_DURATION,
    DEFAULT_JITTER,
    DEFAULT_RING_DURATION,
    NO_PREF,
    PHONE_ID,
    VOIP_PHONE_TEST_TYPE,
    VOIP_PULSE_TEST_TYPE,
} from './VoipTestConstants';
import { isBlank } from '../../properties/funcs';

export const PLACEHOLDERS = {
    PHONE_NUMBER: 'Phone Number',
    EXTENSION: 'Extension',
    USER_ID: 'User Id',
};

const callManagersApiUrl = '/ipm/v1/open/voipcallmanagers/:_id';

/**
 * If this is test now then filter out the agents that are not running
 * @param voipAgents
 */
export function filterVoipAgentsAgents(testNow, voipAgents) {
    // For test now they have to be running
    if (testNow === true) {
        return _.filter(voipAgents, (agent) => agent?.status.toLowerCase() === 'running');
    }

    return voipAgents;
}

export const assignFields = (voipAgents, test, testNow, alertActions, setAgents) => {
    const ipmIconText = 'ipm-';
    const vAgentsById = _.keyBy(voipAgents, '_id');
    const agents = _.map(voipAgents, (agent) => {
        const agentObj = {
            name: agent.name,
            id: agent._id,
            typeIcon: agent.typeIcon,
            isIPMIcon: agent.typeIcon.indexOf(ipmIconText) > -1,
            translatedType: agent.translatedType,
            status: agent.status,
            isNextGeneration: isNextGeneration(agent),
            adapterType: _.get(agent, 'voipConfiguration.adapterType'),
            interfaceTooltip: getAgentVoIPRegistrationInterfaceTooltip(agent),
            interfaceName: getAgentVoIPRegistrationInterface(agent),
            useDirectCalling: isVoIPAgentDirectCallMode(agent),
        };

        return agentObj;
    });

    // Basically check to see if the pulses they used on the test they want to re-run are still there (only for testNow)
    if (!isConfigValid(vAgentsById, test) && testNow) {
        alertActions.createWarningAlert(
            i18n._(
                t`It appears that one or more of the nPoints from the existing test have been deleted, some configuration elements have not been copied over.`
            )
        );

        // We don't have everything we need, so just start fresh.
        test.testConfig.callee = { ids: [] };
        test.testConfig.caller = { ids: [] };

        test.agents = {
            all: false,
            ids: [],
        };

        // May need to reset this too...I didn't in case they call a phone number we'd at least load that up for them.
        // test.testConfig.callVia = {};
    }

    test.quickTestType = test.quickTestType || VOIP_PULSE_TEST_TYPE;
    // MDL - The duration and jitter values need to be strings in the UI for the portal/ui/Material select to work properly
    test.testConfig.duration = test.testConfig.duration ? test.testConfig.duration.toString() : DEFAULT_DURATION;
    test.testConfig.ringDuration = test.testConfig.ringDuration
        ? test.testConfig.ringDuration.toString()
        : DEFAULT_RING_DURATION;
    test.testConfig.jitter = test.testConfig.jitter ? test.testConfig.jitter.toString() : DEFAULT_JITTER;

    setAgents(agents);
    return vAgentsById;
};

function dot(...args) {
    if (_.size(args) === 1 && _.isString(args[0])) {
        args = args[0].split('.');
    } else {
        args = _.flatten(args);
    }

    if (_.isEmpty(args)) return _.identity;

    // Dynamically produce something like _.compose( _.property('2nd'), _.property('1st') ), aka XXX['1st']['2nd']
    const accessors = args.reverse().map(_.property);
    return _.flowRight.apply(null, accessors);
}

export async function getCallManagers(voipAgents, dataService, vAgentsById) {
    const getCallManager = async (id) => {
        const url = buildLinkUrl({ target: callManagersApiUrl }, { _id: id }, null);
        const response = await dataService.get(url);
        return response;
    };

    const ids = _.chain(voipAgents).map(dot('voipConfiguration.primaryCallManager')).uniq().compact().value();

    const promises = ids.map((id) => getCallManager(id));

    const datas = await Promise.all(promises);

    const callManagersById = _.keyBy(datas, '_id');

    _.each(
        vAgentsById,
        (agent) =>
            (agent.voipConfiguration.primaryCallManager = callManagersById[agent.voipConfiguration.primaryCallManager])
    );

    return vAgentsById;
}

/**
 * If we get a server side error the user's selections will be cleared out
 *   Make sure we only reset the caller/callee if it's truly an add new
 * @return {Boolean}
 */
export function isAddNew(reRun, test) {
    return !reRun && _.get(test, 'testConfig.caller', []).length === 0;
}

export const setConfigDefaults = (
    test,
    codecs,
    setCodecs,
    voipAgentsById,
    setVoipConfig,
    setDisableCallViaBody,
    setShowCallViaBody
) => {
    // set working copy
    const config = {};

    // if we have ids in config.callee/caller then we need to assign the correct scope fields
    // _.map([CALLER, CALLEE], setConfigValues(test, config));
    [CALLER, CALLEE].map((item) => setConfigValues(item, test, config, voipAgentsById));

    if (test.quickTestType === VOIP_PHONE_TEST_TYPE) {
        config.callee.phone = true;
        config.callee.showDetails = false;
    }

    // set default codecs and add in No Preference
    // const hasNoPrefs = (_.filter(codecs, { code: '*' }) || []).length > 0;
    const hasNoPrefs = (Object.entries(codecs).filter(([key]) => key === '*') || []).length > 0;
    if (!hasNoPrefs) {
        const newCodecs = { ...NO_PREF, ...codecs };
        setCodecs(newCodecs);
    }

    test.testConfig.preferredCodec = test.testConfig.preferredCodec || '*';

    const callerIds = _.chain(test).get('testConfig.caller.ids').map('id').value();
    const calleeIds = _.chain(test).get('testConfig.callee.ids').map('id').value();

    if (!_.isEmpty(callerIds) && !_.isEmpty(calleeIds)) {
        const callerManager = getCallManagerId(voipAgentsById, _.first(callerIds));
        const calleeManager = getCallManagerId(voipAgentsById, _.first(calleeIds));

        if (_.isEmpty(calleeManager) || calleeManager !== callerManager) {
            const calleeAgent = voipAgentsById[_.first(calleeIds)];
            setDifferentCallManagerSettings(
                { agent: calleeAgent },
                test,
                true,
                setDisableCallViaBody,
                setShowCallViaBody
            );
        }
    }
    // Need to initiate when Re-run pulse to phone test
    if (_.isUndefined(test.testConfig.callVia)) {
        test.testConfig.callVia = {};
    } else if (
        (test.quickTestType === VOIP_PULSE_TEST_TYPE || _.isUndefined(test.quickTestType)) &&
        !_.isUndefined(test.testConfig.callVia.head)
    ) {
        setShowCallViaBody(true);
        setDisableCallViaBody(test.testConfig.callVia.head === CALL_VIA_SIP_USER_ID);
    }

    updateDirectCallingSipUrl(config, test);

    setVoipConfig(config);
};

const getCallManagerId = (voipAgentsById, memberId) => {
    const voipAgent = voipAgentsById[memberId];

    return _.get(voipAgent, 'voipConfiguration.primaryCallManager._id');
};

export function setDifferentCallManagerSettings(config, test, isLoad, setDisableCallViaBody, setShowCallViaBody) {
    //  for ngPulse we don't allow them to edit the phone number, but for TruViewLive we do, so I'm allowing
    //  them to change it for IPM.
    if (config.agent) {
        // MDL - If we are loading the page take what is saved in the config first, then agent, then nothing
        //  if they are just switching agents, then we don't know what they want there, either use the agent value, or show nothing.
        let bodyValue = '';
        if (isLoad === true) {
            bodyValue =
                _.get(test, 'testConfig.callVia.body') || _.get(config, 'agent.voipConfiguration.phoneNumber') || '';
        } else {
            bodyValue = _.get(config, 'agent.voipConfiguration.phoneNumber') || '';
        }

        test.testConfig.callVia.body = bodyValue;
        setDisableCallViaBody(false);
    }

    setShowCallViaBody(true);
}

const setConfigValues = (type, test, config, voipAgentsById) => {
    config[type] = {};
    // Match portal/ui: when quickTestType is undefined OR equals VoipPulse, take id from testConfig.
    if (_.isUndefined(test.quickTestType) || test.quickTestType === VOIP_PULSE_TEST_TYPE) {
        config[type].id = _.get(_.first(test.testConfig[type].ids), 'id');
    }
    if (test.quickTestType === VOIP_PHONE_TEST_TYPE) {
        if (type === CALLER) {
            config[type].id = _.first(test.agents.ids);
        } else {
            config[type].id = PHONE_ID;
        }
    }
    const vConfig = config[type];

    if (!_.isEmpty(vConfig.id) && vConfig.id !== PHONE_ID) {
        vConfig.showDetails = true;
        vConfig.agent = voipAgentsById[vConfig.id];
    }
};

export const updateDirectCallingSipUrl = (vConfig, test) => {
    const caller = vConfig[CALLER];
    const callee = vConfig[CALLEE];
    const callerUseDirectCalling = _.get(caller, 'agent.voipConfiguration.useDirectCalling');
    const calleeUseDirectCalling = _.get(callee, 'agent.voipConfiguration.useDirectCalling');
    if (!callerUseDirectCalling && !calleeUseDirectCalling) {
        return;
    }

    if (callee.id === PHONE_ID) {
        callee.phone = true;
        callee.showDetails = false;
    } else {
        callee.phone = false;
        callee.showDetails = false;
    }

    test.testConfig.callVia = {};
    test.testConfig.callVia.head = CALL_VIA_PHONE_NUMBER;

    const calleeAgent = _.get(vConfig, 'callee.agent');
    const calleeVoipConfiguration = _.get(calleeAgent, 'voipConfiguration');
    if (calleeVoipConfiguration) {
        test.testConfig.callVia.body = calleeVoipConfiguration.phoneNumber;
        test.testConfig.callVia.useDirectCalling = true;
        const interfaces = _.get(calleeAgent, 'network.reportedInterfaces.interfaces');
        const ipVersion = _.get(calleeAgent, 'voipConfiguration.ipVersion', 'ipv4');
        const voipInterface = _.find(interfaces, (itf) => _.get(itf, 'is_selected_voip'));
        if (voipInterface && (voipInterface.ipv4 || voipInterface.ipv6)) {
            const ip =
                ipVersion && ipVersion === 'ipv6'
                    ? `[${voipInterface.ipv6.split('/')[0]}]`
                    : voipInterface.ipv4.split('/')[0];
            if (ip !== '0.0.0.0') {
                test.testConfig.callVia.body += `@${ip}`;
            }
        }
    }
};

/**
 * Just in case the agents they want to re-run this test from are missing...
 * @param indexedAgents
 */
const isConfigValid = (voipAgentsById, test) => {
    // So we may get an array like [null, null] which makes this test kinda tricky...
    const partyAgents = _.filter(test.agents.ids, (agentId) => !_.isEmpty(agentId));

    // If we don't have any agents then it's just a new one.
    if (partyAgents.length === 0) {
        return true;
    }

    // See if we are missing the first agent
    if (!voipAgentsById[_.first(partyAgents)]) {
        return false;
    }

    // See if we are missing the second agent.
    if (partyAgents.length === 2) {
        if (!voipAgentsById[partyAgents[1]]) {
            return false;
        }
    }

    // All good.
    return true;
};

function timeDisplay(time) {
    // expects milliseconds
    if (time <= 60) {
        return `${time}s`;
    }
    return `${time / 60}m`;
}

export function durationDisplayDefault(time, base) {
    if (time.toString() === base) {
        return `Default (${base}s)`;
    }
    return timeDisplay(time);
}

export function getOther(calleeOrCaller) {
    if (calleeOrCaller === CALLEE) {
        return CALLER;
    }
    return CALLEE;
}

/**
 * Used to display a warning alert if one of the selected agents does not have a call manager
 * @param config
 * @returns {boolean}
 */
export function hasManager(config, alertsActions) {
    if (_.get(config, 'agent.voipConfiguration.useDirectCalling')) {
        return true;
    }
    if (config.id && !_.get(config, 'agent.voipConfiguration.primaryCallManager') && config.id !== PHONE_ID) {
        if (config.agent) {
            alertsActions.createWarningAlert(
                i18n._(t`${config.agent.name} does not have a call manager and can not be used in a VoIP test`)
            );
        }

        return false;
    }
    return true;
}

export function setSameCallManagerSettings(test, setDisableCallViaBody, setShowCallViaBody, setPlaceholder) {
    // let them select the call Via
    test.testConfig.callVia = {};
    setDisableCallViaBody(false);
    setShowCallViaBody(false);
    setPlaceholder(PLACEHOLDERS.PHONE_NUMBER);
}

/**
 * Build the testConfig depending on what is selected
 * @param config
 * @param otherConfig
 * @param calleeOrCaller
 */
export function adjustValues(vConfig, config, otherConfig, calleeOrCaller, test, setShowCallViaBody) {
    // NEVER set all=true for VoIP Pulse to Pulse
    test.agents.all = false;

    // caller callee things
    // Pulse to pulse setup
    if (config.id !== PHONE_ID && otherConfig.id !== PHONE_ID) {
        if (!_.isUndefined(otherConfig.id)) {
            test.testConfig[getOther(calleeOrCaller)] = _.extend(test.testConfig[getOther(calleeOrCaller)], {
                ids: [createCallerCalleeId(otherConfig.id, otherConfig.agent.guid)],
            });
        }

        // ITP - if we go back and forth from a voip phone to a voip pulse quick test type the callee testType will be null
        // see line 483, we need to set it up again so we can assign the correct values so the test will save
        if (isBlank(test.testConfig[calleeOrCaller]) && calleeOrCaller === CALLEE) test.testConfig.callee = {};

        vConfig.callee.phone = false;
        if (test.testConfig[calleeOrCaller]) {
            test.testConfig[calleeOrCaller].ids = [createCallerCalleeId(config.id, config.agent.guid)];
            test.testConfig[calleeOrCaller].sipUser = config.agent.voipConfiguration.sipUserId;
            test.testConfig[calleeOrCaller].extension = config.agent.voipConfiguration.extension;
            test.testConfig[calleeOrCaller].ipVersion = config.agent.voipConfiguration.ipVersion;
            if (vConfig[CALLER].agent.voipConfiguration.useDirectCalling) {
                updateDirectCallingSipUrl(vConfig, test);
            } else {
                test.testConfig[calleeOrCaller].domain = config.agent.voipConfiguration.primaryCallManager.domain;
                test.testConfig[calleeOrCaller].sipProxy = config.agent.voipConfiguration.primaryCallManager.sipProxy;
                test.testConfig[calleeOrCaller].sipPort = config.agent.voipConfiguration.primaryCallManager.sipPort;
                test.testConfig[calleeOrCaller].rtpStartPort =
                    config.agent.voipConfiguration.primaryCallManager.rtpStartPort;
                test.testConfig[calleeOrCaller].rtpEndPort =
                    config.agent.voipConfiguration.primaryCallManager.rtpEndPort;
            }
        }
        test.quickTestType = VOIP_PULSE_TEST_TYPE;
        // at the end of the day we also want the ids in the agents block
        test.agents.ids = []
            .concat(_.map(test.testConfig.callee.ids, 'id'))
            .concat(_.map(test.testConfig.caller.ids, 'id'));
    } else {
        test.testConfig.callee = null;
        // Set up the caller object
        if (config.id && otherConfig.id) {
            test.testConfig.caller = {
                ids: [
                    config.id === PHONE_ID
                        ? createCallerCalleeId(otherConfig.id, otherConfig.agent.guid)
                        : createCallerCalleeId(config.id, config.guid),
                ],
            };
        }
        test.agents.ids = _.map(test.testConfig.caller.ids, 'id');
        test.quickTestType = VOIP_PHONE_TEST_TYPE;
        vConfig.callee.phone = true;
        setShowCallViaBody(false);
        vConfig.callee.showDetails = false;
    }
}

function createCallerCalleeId(id, guid) {
    return {
        id,
        guid,
    };
}
